import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ApolloProvider } from '@apollo/client';
import isEqual from 'lodash/isEqual';

import { configApiRef, useAnalytics, useApi } from '@backstage/core-plugin-api';

import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';

import { DialogProvider } from '../../components';
import { Region } from '../../utils';
import { AuthFactoryProvider } from '../auth-factory';
import { CLIENT_ID } from '../auth-factory/auth0/auth';
import { useConfidenceApolloClient } from './useConfidenceApolloClient';

export type ConfidenceCoreContextType = {
  config: Record<string, unknown>;
  setConfig: (v: Record<string, unknown>) => void;
  displayNames: Map<string, string>;
  setDisplayName: (v: Record<string, string>) => void;
  region?: Region;
  isLocalGraphql: boolean;
};

type ProviderConfig = {
  clientId?: string;
  redirectUri?: string;
  [key: string]: unknown;
};

const defaultProviderConfig: ProviderConfig = {
  clientId: CLIENT_ID,
};

const defaultValue: ConfidenceCoreContextType = {
  config: defaultProviderConfig,
  setConfig: () => {},
  displayNames: new Map(),
  setDisplayName: () => {},
  isLocalGraphql: false,
};

const ConfidenceCoreContext =
  createContext<ConfidenceCoreContextType>(defaultValue);

export const ConfidenceCoreProvider = ({
  config: { clientId = CLIENT_ID, redirectUri, ...c } = defaultProviderConfig,
  children,
}: PropsWithChildren<{
  config?: ProviderConfig;
}>) => {
  const [token, setToken] = useState<string>();
  const [region, setRegion] = useState<Region>();
  const [config, setConfig] = useState(c);
  const analyticsApi = useAnalytics();

  const { enabled: requestAccounting } = useFeatureFlag(
    'graphql-request-timing',
    { enabled: false },
  );

  const appConfig = useApi(configApiRef);

  const [displayNames, setDisplayNames] = useState<
    ConfidenceCoreContextType['displayNames']
  >(defaultValue.displayNames);

  const handleTokenReceived = useCallback(
    (userToken: string, userRegion: Region) => {
      setToken(userToken);
      setRegion(userRegion);
    },
    [],
  );

  const uri = useMemo(
    () =>
      appConfig.getOptionalString('confidence.graphql') ??
      'https://graphql-konfidens.spotify.com/graphql',
    [appConfig],
  );

  const isLocalGraphql =
    process.env.NODE_ENV === 'development' && uri.includes('localhost');

  const apolloClient = useConfidenceApolloClient({
    token,
    uri,
    region,
    analyticsApi,
    requestAccounting,
    isLocalGraphql,
  });

  const updateDisplayName = useCallback(
    (v: Record<string, string>) => {
      setDisplayNames(current => {
        const newMap = new Map(current);
        Object.entries(v).forEach(([key, value]) => {
          newMap.set(key, value);
        });
        if (isEqual(current, newMap)) {
          return current;
        }
        return newMap;
      });
    },
    [setDisplayNames],
  );

  const value = useMemo<ConfidenceCoreContextType>(
    () => ({
      config,
      setConfig,
      displayNames,
      setDisplayName: updateDisplayName,
      region,
      isLocalGraphql,
    }),
    [config, displayNames, updateDisplayName, region, isLocalGraphql],
  );

  return (
    <ConfidenceCoreContext.Provider value={value}>
      <AuthFactoryProvider
        clientId={clientId}
        redirectUri={redirectUri}
        onTokenReceived={handleTokenReceived}
      >
        <ApolloProvider client={apolloClient}>
          <DialogProvider>{children}</DialogProvider>
        </ApolloProvider>
      </AuthFactoryProvider>
    </ConfidenceCoreContext.Provider>
  );
};

export const useConfidenceCoreContext = () => useContext(ConfidenceCoreContext);

export const useDisplayNames = () => {
  const { displayNames, setDisplayName } = useConfidenceCoreContext();

  return {
    displayNames,
    setDisplayName,
  };
};

export const useIsLocalGraphql = () => {
  const { isLocalGraphql } = useConfidenceCoreContext();
  return isLocalGraphql;
};

export const useRegion = () => {
  const { region } = useConfidenceCoreContext();
  return region;
};
